import React from 'react'
import { ProductData, Category } from 'lib/types'
import { usePathResolver } from 'lib/core/hooks'
import { useDocument } from 'lib/prismic'
import { NoMatch } from 'lib/shared/scenes/NoMatch'
import { EmptyContainer, Spinner } from 'lib/ui'
import { ProductResolver, CategoryResolver } from 'lib/modules/catalog'
import { useLocation } from 'react-router-dom'
import { useWordpressPage } from 'lib/wp/useWordpressPage'

export interface Props {
  components: {
    Category: (props: { category: Category }) => JSX.Element | null
    CmsPage: () => JSX.Element | null
    Product: (props: { product: ProductData }) => JSX.Element | null
    NoMatch?: () => JSX.Element | null
    PrismicPage?: (props: any) => JSX.Element | null
    WordpressPage?: (props: any) => JSX.Element | null
  }
  prismicPageType?: string[]
}

export const DynamicRoute = ({ components, prismicPageType = ['page'] }: Props) => {
  const { loading, entity, slug } = usePathResolver()
  const location = useLocation()
  const { pathname } = location
  const { loading: wordpressLoading, data: page } = useWordpressPage({
    pageURI: pathname.replace(/^(\/)/, ''),
    enabled: !!components.WordpressPage
  })
  const { loading: prismicLoading, document } = useDocument<any>({
    uid: slug,
    types: prismicPageType,
    enabled: !!components.PrismicPage
  })

  const NoMatchComponent = components.NoMatch ?? NoMatch

  const renderLoading = () => (
    <EmptyContainer>
      <Spinner />
    </EmptyContainer>
  )

  if (loading) {
    return renderLoading()
  }

  if (entity === 'category') {
    return (
      <CategoryResolver>
        {({ category }) => <components.Category category={category} />}
      </CategoryResolver>
    )
  }

  if (entity === 'page') {
    return <components.CmsPage />
  }

  if (entity === 'product') {
    return (
      <ProductResolver>
        {({ product }) => <components.Product product={product} />}
      </ProductResolver>
    )
  }

  if (components.PrismicPage) {
    if (document) {
      return <components.PrismicPage {...document} />
    }

    if (prismicLoading) {
      return renderLoading()
    }
  }

  if (components.WordpressPage) {
    if (page?.page) {
      return <components.WordpressPage {...page} />
    }

    if (wordpressLoading) {
      return renderLoading()
    }
  }

  return (
    <NoMatchComponent />
  )
}
